import { useMemo } from 'react'
import { useLocation } from 'react-router'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ShareModal from 'components/Common/Modals/ShareModal/ShareModal'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import config from 'constants/config'
import { LOYALTY_CREDIT_CARD_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { useAppSelector } from 'hooks/reduxHooks'
import styled, { useTheme } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const NAV_ANCHORS = ['overview', 'benefits', 'travel-perks', 'how-to-apply', 'faqs'] as const

const NavOverlay = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: ${rem(12)};
  padding-inline: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  position: relative;
  z-index: 10;

  ${mediaQueryUp.desktop} {
    position: absolute;
    top: 0;
    inset-inline: 0;
    padding-block: ${rem(20)};
    padding-inline: ${rem(20)} ${rem(32)};
    background-color: transparent;
  }
`

const HomeLink = styled(ClickableLink)`
  text-decoration: none;
  color: inherit;
`

function LoyaltyCreditCardNav() {
  const intl = useIntl()
  const theme = useTheme()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const { pathname } = useLocation()

  const navItems = useMemo(() => NAV_ANCHORS.map(anchor => ({
    anchor,
    to: `${pathname}#${anchor}`,
    label: {
      overview: intl.formatMessage({
        id: 'YNrQq-tvevp1okidXjlE',
        defaultMessage: 'Overview',
        description: 'Anchor navigation link for the overview section on the Société Credit Card landing page',
      }),
      benefits: intl.formatMessage({
        id: 'HlWkwkuTWzZXz8vZGgpR',
        defaultMessage: 'Features',
        description: 'Anchor navigation link for the credit card features section on the Société Credit Card landing page',
      }),
      'travel-perks': intl.formatMessage({
        id: 'apHrlZVmZn6fWnGzlJ9X',
        defaultMessage: 'Travel Perks',
        description: 'Anchor navigation link for the travel perks section on the Société Credit Card landing page',
      }),
      'how-to-apply': intl.formatMessage({
        id: 'wAC1jLemxO5Q4RHezHjk',
        defaultMessage: 'How to Apply',
        description: 'Anchor navigation link for the application instructions section on the Société Credit Card landing page',
      }),
      faqs: intl.formatMessage({
        id: 'ufCFl1wEiwwoCcpxFFuX',
        defaultMessage: 'FAQs',
        description: 'Anchor navigation link for the frequently asked questions section on the Société Credit Card landing page',
      }),
    }[anchor],
  })), [intl, pathname])

  const shareConfig = useMemo<ShareConfig>(() => ({
    url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/${regionCode.toLowerCase()}/${LOYALTY_CREDIT_CARD_PAGE}`,
    shareTitle: intl.formatMessage({
      id: 'oZHCptD1n0GF_LzkSzuu',
      defaultMessage: 'Société Credit Card',
      description: 'Social share title for the Société Credit Card landing page',
    }),
    shareMessage: intl.formatMessage({
      id: 'zyjY4Sbt9UMdbIQr0Ydb',
      defaultMessage: 'Check out the new Société Credit Card from Luxury Escapes.',
      description: 'Social share message body for the Société Credit Card landing page',
    }),
    pageType: 'loyalty',
    pageId: 'societe-credit-card',
  }), [intl, regionCode])

  const homeLabel = intl.formatMessage({
    id: 'WioYSEBTbtDN5Q4luzSh',
    defaultMessage: 'Home',
    description: 'Navigation bar link label to return to the main homepage from the LE Live event page',
  })

  const shareModalTitle = intl.formatMessage({
    id: 'tOuFua2YZaiUnrXIGzg5',
    defaultMessage: 'Share the Société Credit Card',
    description: 'Heading shown at the top of the share dialog opened from the Société Credit Card landing page navigation bar.',
  })

  return (
    <NavOverlay>
      <HomeLink to="/">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <LineArrowLeftIcon colour="normal-contrast" />
          <LuxuryEscapesSmallLogo fill={theme.palette.highlight.primary.darkForeground} height={28} />
          <BodyText variant="large" weight="bold" colour="normal-contrast">
            {homeLabel}
          </BodyText>
        </Group>
      </HomeLink>

      <CSSBreakpoint max="tablet" dynamic>
        <LoyaltyCreditCardApplyNowButton
          size="small"
          placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.nav}
        >
          {intl.formatMessage({
            id: 'U9X8MMosEjGLO9V3ZyR1',
            defaultMessage: 'Apply',
            description: 'Apply button label shown in the mobile Société Credit Card navigation bar, linking to the credit card application form.',
          })}
        </LoyaltyCreditCardApplyNowButton>
      </CSSBreakpoint>

      <CSSBreakpoint min="desktop" dynamic>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          {navItems.map(item => (
            <TextButton
              key={item.anchor}
              kind="tertiary"
              size="large"
              to={item.to}
            >
              {item.label}
            </TextButton>
          ))}
          <ShareModal shareConfig={shareConfig} hideEmail modalTitle={shareModalTitle}>
            <TextButton
              kind="secondary"
              size="large"
              startIcon={<LineShareAltIcon />}
            >
              {intl.formatMessage({
                id: '28Fde9ffpMvc0gT9fcnG',
                defaultMessage: 'Share',
                description: 'Share button label in the Société Credit Card navigation bar',
              })}
            </TextButton>
          </ShareModal>
        </Group>
      </CSSBreakpoint>
    </NavOverlay>
  )
}

export default LoyaltyCreditCardNav
